import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import useDateFilter from 'src/hooks/useDateFilter';
import useFeatures from 'src/hooks/useFeatures';
import useMerchant from 'src/hooks/useMerchant';
import { GetPurchasesQueryParams, getPurchasesByMerchantCodeApi } from 'src/api/purchaseApi';
import { exportPurchases } from 'src/api/dataExportApi';
import { PATH_PAGE } from 'src/routes/paths';
import { TransactionOwnershipType, TransactionsSearchCriteria, TransactionsPage } from 'src/@types/transactions';
import { downloadFile } from 'src/utils/download';

//
//

const emptyTransactionsPage: TransactionsPage = {
  content: [],
  totalElements: 0,
  totalPages: 0,
};

export type ITransactionState = {
  downloadTransactions: (searchCriteria: TransactionsSearchCriteria, additionalDownloadFields:string[]) => void;
  getTransactionsByMerchant: (searchCriteria: TransactionsSearchCriteria) => void;
  triggerLoading: boolean;
  setTriggerLoading: (value: boolean) => void;
  isDownloading: boolean;
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (value: number) => void;
  transactionsPage: TransactionsPage;
  transactionsPerPage: number;
};

export const TransactionContext = React.createContext<ITransactionState>({
  downloadTransactions: () => {},
  getTransactionsByMerchant: () => {},
  triggerLoading: false,
  setTriggerLoading: () => {},
  isDownloading: false,
  isLoading: true,
  currentPage: 1,
  setCurrentPage: () => {},
  transactionsPage: emptyTransactionsPage,
  transactionsPerPage: 10,
} as ITransactionState);

export const TransactionProvider = (props: any) => {
  const navigate = useNavigate();

  const { merchant } = useMerchant();
  const { displayCustomerEmailFeature, isFeaturesLoading } = useFeatures();
  const { dateFrom, dateTo } = useDateFilter();

  const [transactionsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  const [transactionsPage, setTransactionsPage] = useState<TransactionsPage>({
    content: [],
    totalElements: 0,
    totalPages: 0,
  });

  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [triggerLoading, setTriggerLoading] = useState(false);

  const buildSearchParams = (searchCriteria: TransactionsSearchCriteria) => {
    const withoutProgramme = searchCriteria?.withoutProgramme ? searchCriteria?.withoutProgramme : undefined;
    const emailParam = searchCriteria?.emailFilter ? searchCriteria.emailFilter : undefined;
    const programmeCodeParam = searchCriteria?.programmeCode ? searchCriteria.programmeCode : undefined
    const emailField =
      displayCustomerEmailFeature && !isFeaturesLoading
        ? ('emailAddress' as const)
        : ('maskedEmailAddress' as const);
    let newVar: GetPurchasesQueryParams = {
      ownershipType: searchCriteria.transactionOwnershipTypes,
      merchantCode: merchant?.code,
      page: currentPage,
      size: transactionsPerPage,
      dateFrom,
      dateTo,
      footprintTypeExclude: searchCriteria.footprintTypeExclude,
      email: emailParam?.trim(),
      fields: [emailField],
      programmeCode: programmeCodeParam,
      withoutProgramme,
    };
    return newVar;
  };
//
  const downloadTransactions = (searchCriteria: TransactionsSearchCriteria, additionalDownloadFields:string[]) => {
    const params = buildSearchParams(searchCriteria);
    params.fields = params.fields || [];
    params.fields.push(
      'purchaseId',
      'orderReference',
      'providerReference',
      'transactionDate',
      'co2Amount',
      'cost',
      'currency'
    );
    params.fields = params.fields.concat(additionalDownloadFields);

      setIsDownloading(true);
    exportPurchases(params)
      .then((response) => {
        let path;
        if(searchCriteria.transactionOwnershipTypes.length === 1) {
            path = searchCriteria.transactionOwnershipTypes[0] === TransactionOwnershipType.MERCHANT ? 'credits' : 'customertransactions';
        }
        else{
            path = 'credits-and-customertransactions';
        }
        const filename = `${path}-${new Date().toISOString().slice(0, 16).replace(/[-T:]/g, '')}.csv`;
        downloadFile(response.data, filename);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  const getTransactionsByMerchant = (searchCriteria: TransactionsSearchCriteria) => {
    if (merchant?.code) {
      setIsLoading(true);
      const params = buildSearchParams(searchCriteria);
      !isFeaturesLoading &&
        getPurchasesByMerchantCodeApi(params)
          .then((transactions) => setTransactionsPage(transactions?.data))
          .catch((error) => {
            if (error.status === 403) {
              navigate(PATH_PAGE.page403);
            }
          })
          .finally(() => setIsLoading(false));
    }
    setTriggerLoading(false);
  };

  return (
    <TransactionContext.Provider
      value={{
        downloadTransactions,
        getTransactionsByMerchant,
        triggerLoading,
        setTriggerLoading,
        isDownloading,
        isLoading,
        currentPage,
        setCurrentPage,
        transactionsPage,
        transactionsPerPage,
      }}
    >
      {props.children}
    </TransactionContext.Provider>
  );
};
